import { useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { WhatsAppFab } from "@/components/WhatsAppFab";
import { AssistantWidget } from "@/components/assistant/AssistantWidget";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { MaintenanceOverlay } from "@/components/MaintenanceOverlay";
import { InstallPrompt } from "@/components/InstallPrompt";
import { EnablePushBanner } from "@/components/EnablePushBanner";
import { usePublicSettings } from "@/hooks/use-settings";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Booking from "@/pages/Booking";
import About from "@/pages/About";
import Terms from "@/pages/Terms";
import Login from "@/pages/Login";
import Orders from "@/pages/Orders";
import OrderDetails from "@/pages/OrderDetails";
import EditOrder from "@/pages/EditOrder";
import Addresses from "@/pages/Addresses";
import Profile from "@/pages/Profile";
import Wallet from "@/pages/Wallet";
import TopUpWallet from "@/pages/TopUpWallet";
import TopUpSuccess from "@/pages/TopUpSuccess";
import Gifts from "@/pages/Gifts";
import GiftSuccess from "@/pages/GiftSuccess";
import GiftBooking from "@/pages/GiftBooking";
import RedeemGift from "@/pages/RedeemGift";
import Notifications from "@/pages/Notifications";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentFailure from "@/pages/PaymentFailure";
import NotFound from "@/pages/not-found";

function HideWhatsAppOnBooking({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  if (location === '/booking') {
    return null;
  }
  return <>{children}</>;
}

function ScrollToTop() {
  const [location] = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  
  return null;
}

function Router() {
  return (
    <>
      <ScrollToTop />
      <Switch>
        <Route path="/" component={Home} />
        {/* Shareable assistant links: the home page with the chat open (AssistantWidget). */}
        <Route path="/assistant" component={Home} />
        <Route path="/ai" component={Home} />
        <Route path="/services" component={Services} />
        <Route path="/login" component={Login} />
        <Route path="/booking">
          <ProtectedRoute>
            <Booking />
          </ProtectedRoute>
        </Route>
        <Route path="/orders">
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        </Route>
        <Route path="/orders/:id">
          <ProtectedRoute>
            <OrderDetails />
          </ProtectedRoute>
        </Route>
        <Route path="/orders/:id/edit">
          <ProtectedRoute>
            <EditOrder />
          </ProtectedRoute>
        </Route>
        <Route path="/notifications">
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        </Route>
        <Route path="/addresses">
          <ProtectedRoute>
            <Addresses />
          </ProtectedRoute>
        </Route>
        <Route path="/profile">
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        </Route>
        <Route path="/wallet">
          <ProtectedRoute>
            <Wallet />
          </ProtectedRoute>
        </Route>
        <Route path="/topup">
          <ProtectedRoute>
            <TopUpWallet />
          </ProtectedRoute>
        </Route>
        <Route path="/topup/success" component={TopUpSuccess} />
        <Route path="/gifts">
          <ProtectedRoute>
            <Gifts />
          </ProtectedRoute>
        </Route>
        <Route path="/gift-success" component={GiftSuccess} />
        <Route path="/gift-booking" component={GiftBooking} />
        <Route path="/redeem">
          <ProtectedRoute>
            <RedeemGift />
          </ProtectedRoute>
        </Route>
        <Route path="/payment/success" component={PaymentSuccess} />
        <Route path="/payment/failure" component={PaymentFailure} />
        <Route path="/about" component={About} />
        <Route path="/terms" component={Terms} />
        <Route component={NotFound} />
      </Switch>
    </>
  );
}

/**
 * Renders the maintenance overlay if maintenance mode is on, otherwise the
 * normal site. Must be INSIDE the QueryClientProvider so usePublicSettings
 * (a useQuery) can access the QueryClient.
 */
function AppContent() {
  const { data: publicSettings } = usePublicSettings();

  if (publicSettings?.maintenanceMode) {
    return <MaintenanceOverlay message={publicSettings.maintenanceMessage} />;
  }

  return (
    <>
      <Navigation />
      <main>
        <Router />
      </main>
      <HideWhatsAppOnBooking>
        <WhatsAppFab />
      </HideWhatsAppOnBooking>
      <AssistantWidget />
      <InstallPrompt />
      <EnablePushBanner />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AppContent />
        <Toaster />
        <Footer />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
